import _ from "lodash";
import { EquipmentReportBlock, EquipmentBlockFilters, EquipmentTemplate, TabType, TemplateType, EquipmentTemplateBlock } from "../../stores/TabsStore";
import { DateTime } from "luxon";
import { DateRangeDateTime, Equipment, FShift, Person } from "../../types/types";


function getDefaultBlockPosition(index: number) {
    return {
        i: index + 1,
        x: (index * 3) % 12,
        y: Infinity,
        w: 3,
        h: 1,
        minW: 2,
        minH: 1,
        resizeHandles: ["se"]
    }
}

export function getBaseTemplateForTabType(tabType: TabType):TemplateType {
    switch (tabType) {
        case "equipments": return "equipments_free_template"
        case "persons": return "persons_free_template"
        case "divisions": return "divisions_free_template"
        default: return tabType;
    }
}

export function generateBlocksByEquipmentTemplate(data: EquipmentBlockFilters, template: EquipmentTemplate, shifts: FShift[], equipments: Equipment[] | null, persons: Person[] | null):EquipmentReportBlock[] {
    if(!data || !template || !template.data || template.data.length === 0) {return []}
    let temp_blocks: EquipmentTemplateBlock[] = []
    for(let tblock of template.data) {
        let defaultPosition = getDefaultBlockPosition(temp_blocks.length)
        let position = {
            ...defaultPosition,
            ...tblock.position,
            resizeHandles: tblock.position?.resizeHandles ? tblock.position.resizeHandles : defaultPosition.resizeHandles
        }
        let temp_block = {
            ...tblock, 
            name: `${tblock.name}`,
            position,
            options: tblock.options,
            type: tblock.type,
            functions: tblock.functions,
            filters: {
                days: data.days,
                weekdays: data.weekdays,
                shifts: data.shifts,
                equipments: data.equipments
            },
            content: tblock.content
        }

        if(temp_block.functions && temp_block.functions.length > 0 && temp_block.functions.includes("splitByShifts")) {
            splitByShifts(data).map(i => (
                temp_blocks.push({
                    ...temp_block,
                    name: `${temp_block.name} ${i.name}`,
                    filters: {...temp_block.filters, shifts: i.shifts}
                })
            ))
        } else if(temp_block.functions && temp_block.functions.length > 0 && temp_block.functions.includes("splitByEquipment")) {
            splitByEquipment(data).map(i => (
                temp_blocks.push({
                    ...temp_block,
                    name: `${temp_block.name} ${i.name}`,
                    filters: {...temp_block.filters, equipments: i.equipments}
                })
            ))
        } else {
            temp_blocks.push(temp_block)
        }
    }
    let data_blocks: EquipmentTemplateBlock[] = []
    
    for(let tb of temp_blocks) {
        if(tb.functions && tb.functions.length > 0) {     
            tb.functions.map(func => {
                switch (func) {
                    case "getFirstShift":
                        let gfs = getFirstShift(data)
                        tb = {
                            ...tb,
                            name: `${tb.name} ${shifts.find(s => s.id === gfs.name)?.name}`,
                            filters: {...tb.filters, shifts: gfs.shifts} as EquipmentBlockFilters
                        }  
                        break;                 
                    case "getLastShift":
                        let gls = getLastShift(data)
                        tb = {
                            ...tb,
                            name: `${tb.name} ${shifts.find(s => s.id === gls.name)?.name}`,
                            filters: {...tb.filters, shifts: gls.shifts} as EquipmentBlockFilters
                        }
                        break; 
                    case "getFirstEquipment":
                        let gfe = getFirstEquipment(data)
                        tb = {
                            ...tb,
                            name: `${tb.name} ${equipments?.find(e => e.id === gfe.name)?.attributes.name}`,
                            filters: {...tb.filters, equipments: gfe.equipments} as EquipmentBlockFilters
                        }
                        break; 
                    case "getLastEquipment":
                        let gle = getLastEquipment(data)
                        tb = {
                            ...tb,
                            name: `${tb.name} ${equipments?.find(e => e.id === gle.name)?.attributes.name}`,
                            filters: {...tb.filters, equipments: gle.equipments} as EquipmentBlockFilters
                        } 
                        break; 
                    case "getDateTimeRange":
                        let dtr = getDateTimeRange(data)
                        tb = {
                            ...tb,
                            options: {...tb.options, date_time_range: dtr}
                        }
                        break; 
                    default: break;
                }
                
            })
            data_blocks.push(tb)
        } 
        else {
            data_blocks.push(tb)
        }
    }
    return data_blocks.flat().map((block, index) => ({...block, id: index + 1})) as EquipmentReportBlock[]
}

function getDateTimeRange(data: EquipmentBlockFilters):DateRangeDateTime {
    let orderedDays = _.orderBy(data.days)
    let startDate = DateTime.fromISO(orderedDays[0]).toSQL()
    let finishDate = DateTime.fromISO(orderedDays[orderedDays.length - 1]).toSQL()
    return {from: startDate!, to: finishDate!}
}

function getFirstShift(data: EquipmentBlockFilters):{name: string, shifts: string[]} {
    return ({name: data.shifts[0], shifts: [data.shifts[0]]})
}

function getLastShift(data: EquipmentBlockFilters):{name: string, shifts: string[]} {
    return ({name: data.shifts[data.shifts.length - 1], shifts: [data.shifts[data.shifts.length - 1]]})
}

function splitByShifts(data: EquipmentBlockFilters):{name: string, shifts: string[]}[] {
    return data.shifts.map(shift => (
        {name: shift, shifts: [shift]}
    ))
}

function getFirstEquipment(data: EquipmentBlockFilters):{name: string, equipments: string[]} {
    return ({name: data.equipments[0], equipments: [data.equipments[0]]})
}

function getLastEquipment(data: EquipmentBlockFilters):{name: string, equipments: string[]} {
    return ({name: data.equipments[data.equipments.length - 1], equipments: [data.equipments[data.equipments.length - 1]]})
}

function splitByEquipment(data: EquipmentBlockFilters):{name: string, equipments: string[]}[] {
    return data.equipments.map(shift => (
        {name: shift, equipments: [shift]}
    ))
}


export const templates = [
    {
        name: "Отчет по потерям",
        slug: "equipments_idle_report",
        tabType: "equipments",
        data: [
            {
                name: "Состоявшиеся смены",
                type: "calculated_block",
                options: {
                    calculation_type: "shifts",
                    active_rows: true
                },
                content: ""
            },
            {
                name: "Общий простой",
                type: "calculated_block",
                options: {
                    calculation_type: "idle",
                    active_rows: false
                },
                content: ""
            },
            {
                name: "Время ремонта",
                type: "calculated_block",
                options: {
                    calculation_type: "repair",
                    active_rows: false
                },
                content: ""
            },
            {
                name: "Время сервиса",
                type: "calculated_block",
                options: {
                    calculation_type: "service",
                    active_rows: false
                },
                content: ""
            },
            {
                name: "Потери в начале смен",
                type: "calculated_block",
                options: {
                    calculation_type: "equipment_start",
                    active_rows: false
                },
                content: "<p></p>"
            },
            {

                name: "Потери в конце смен",
                type: "calculated_block",
                options: {
                    calculation_type: "equipment_end",
                    active_rows: false
                },
                content: ""
            },
            {
                name: "Разгон и торможение",
                type: "equipment_start_end_block",
                options: {
                    calculation_type: "load",
                    active_rows: true
                },
                content: ""
            }
        ]
    },
    {
        name: "Общий отчет о загрузке оборудования",
        slug: "equipments_load_report",
        tabType: "equipments",
        data: [
            {
                name: "Общая загрузка",
                type: "calculated_block",
                functions: undefined,
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: ""
            },
            {
                name: "Загрузка смена: ",
                type: "calculated_block",
                functions: ["splitByShifts"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: ""
            },
            {
                name: "Динамика загрузки оборудования за период",
                type: "bar_chart",
                options: {
                    split_by_shifts: false,
                    active_rows: true
                },
                content: ""
            },
            {
                name: "Загрузка смен в сравнении",
                type: "line_chart",
                options: {
                    split_by_shifts: true,
                    active_rows: true
                },
                content: ""
            },
            {
                name: "Загрузка оборудования по часам",
                type: "heat_map_by_hours",
                options: {
                    split_by_shifts: false,
                    active_rows: true
                },
                content: ""
            }
        ]
    },
    {
        name: "Отчет по единице оборудования",
        slug: "equipments_equipment_report",
        tabType: "equipments",
        data: [
            {
                name: "Состоявшиеся смены",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "shifts",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Название блока",
                type: "text_block",
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p><strong>Блок: Анализ загрузки оборудования</strong></p><p>Представленные данные позволяет оценить организацию работ по загрузке ресурса.</p><p></p>"
            },
            {
                name: "Загрузка общая",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Загрузка по смене: ",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Динамика работы по дням",
                type: "bar_chart",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Динамика загрузки по сменам",
                type: "line_chart",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: true,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Тепловая карта работы оборудования",
                type: "heat_map",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Блок: Анализ потерь оборудования",
                type: "text_block",
                options: {
                    calculation_type: "idle",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p><strong>Блок: Анализ потерь оборудования</strong></p>"
            },
            {
                name: "Простой общий",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "idle",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Простой по смене:",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "idle",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Сервисные работы",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "service",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Ремонтные работы",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "repair",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Переналадки",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "adjustment",
                    split_by_shifts: false,
                    active_rows: true
                },
                content: "<p></p>"
            },
            {
                name: "Потери в начале смен",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "equipment_start",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "Потери в конце смен",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "equipment_end",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "График разгона и торможения оборудования",
                type: "equipment_start_end_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "Название блока",
                type: "text_block",
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p><strong>Блок: Анализ взаимодействия с оборудованием</strong></p>"
            },
            {
                name: "Общее взаимодействие (с учетом ремонта, сервиса, перенадалок)",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "interaction",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "Ручное взаимодействие",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "manual",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "Переналадки",
                type: "calculated_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "adjustment",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "Список взаимодействий",
                type: "interactions_table_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            },
            {
                name: "Параметрический анализ",
                type: "text_block",
                functions: ["getFirstEquipment"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p><strong>Параметрический анализ оборудования</strong></p>"
            },
            {
                name: "Анализ графика тока",
                type: "parametric_graph_block",
                functions: ["getFirstEquipment", "getDateTimeRange"],
                options: {
                    calculation_type: "load",
                    split_by_shifts: false,
                    active_rows: false
                },
                content: "<p></p>"
            }
        ]
    },
    {
        name: "Свободный отчет по оборудованию",
        slug: "equipments_free_template",
        tabType: "equipments",
        data: [] 
    },
    {
        name: "Свободный отчет по персоналу",
        slug: "persons_free_template",
        tabType: "persons",
        data: [] 
    },
    {
        name: "Свободный отчет по подрозделению",
        slug: "divisions_free_template",
        tabType: "divisions",
        data: [] 
    }
]
